import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import ThemeToggle from "./ThemeToggle";
import GcrLogo from "./GcrLogo";
import {
  ChartLineUp, SquaresFour, Trophy, Gear, SignOut, Crown,
  User as UserIcon, Plus, ListChecks, ChartBar, UserCircle, Users, ChatText,
  CurrencyDollar, Envelope, CreditCard, Robot, Stack, UsersThree, Headset, MegaphoneSimple,
} from "@phosphor-icons/react";

const navUserKeys = [
  { to: "/dashboard",    labelKey: "nav.dashboard",   icon: SquaresFour },
  { to: "/jugadas",      labelKey: "nav.picks",        icon: Trophy },
  { to: "/parlays",      labelKey: "nav.parlays",      icon: Stack },
  { to: "/performance",  labelKey: "nav.performance",  icon: ChartLineUp },
  { to: "/comunidad",    labelKey: "nav.community",    icon: UsersThree },
  { to: "/chat",         labelKey: "nav.chat",         icon: Robot },
  { to: "/plans",        labelKey: "nav.plans",        icon: Crown },
  { to: "/profile",      labelKey: "nav.profile",      icon: UserCircle },
];

const navAdminKeys = [
  { to: "/admin",                    labelKey: "nav.panel",           icon: ChartBar },
  { to: "/admin/analisis/nuevo",     labelKey: "nav.new_analysis",    icon: Plus },
  { to: "/admin/analisis",           labelKey: "nav.management",      icon: ListChecks },
  { to: "/admin/parlays",            labelKey: "nav.parlays",         icon: Stack },
  { to: "/admin/usuarios",           labelKey: "nav.users",           icon: Users },
  { to: "/admin/pagos",              labelKey: "nav.payments",        icon: CurrencyDollar },
  { to: "/admin/metodos-pago",       labelKey: "nav.payment_methods", icon: CreditCard },
  { to: "/admin/reviews",            labelKey: "nav.reviews",         icon: ChatText },
  { to: "/admin/soporte",            labelKey: "nav.support",         icon: Headset },
  { to: "/admin/emails",             labelKey: "nav.emails",          icon: Envelope },
  { to: "/admin/broadcast",          labelKey: "nav.broadcast",       icon: MegaphoneSimple },
  { to: "/admin/config",             labelKey: "nav.config",          icon: Gear },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  // Rutas compartidas que mantienen el contexto admin en la sidebar
  const ADMIN_CONTEXT_ROUTES = ["/comunidad", "/chat"];
  const isSharedAdminRoute = user?.role === "admin" &&
    ADMIN_CONTEXT_ROUTES.some(r => location.pathname.startsWith(r));
  const isAdminContext = isAdminRoute || isSharedAdminRoute;

  const links = isAdminContext ? navAdminKeys : navUserKeys;

  return (
    <aside className="sidebar-root hidden lg:flex lg:flex-col w-64 flex-shrink-0 h-full bg-obsidian-900 border-r border-white/[0.06] px-5 py-6 overflow-y-auto">
      <div className="flex items-center gap-2.5 mb-10">
        <GcrLogo size={72} />
      </div>

      {user?.role === "admin" && (
        <div className="flex gap-1 p-1 bg-obsidian-800 border border-white/[0.06] rounded-md mb-5">
          <button
            data-testid="sidebar-mode-user"
            onClick={() => nav(isSharedAdminRoute ? "/admin" : "/dashboard")}
            className={`flex-1 text-xs py-1.5 rounded font-semibold transition-colors ${
              !isAdminContext ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t("nav.user_mode")}
          </button>
          <button
            data-testid="sidebar-mode-admin"
            onClick={() => nav("/admin")}
            className={`flex-1 text-xs py-1.5 rounded font-semibold transition-colors ${
              isAdminContext ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            Admin
          </button>
        </div>
      )}

      <div className="text-[10px] text-zinc-600 tracking-[0.2em] uppercase font-bold mb-3 px-3">
        {isAdminContext ? t("nav.admin_panel_label") : t("nav.navigation_label")}
      </div>

      <nav className="flex flex-col gap-1">
        {links.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`sidebar-link-${to.replace(/\//g, "-")}`}
            end={to === "/admin"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? "text-[#D4AF37]"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`
            }
            style={({ isActive }) => isActive ? {
              background: "linear-gradient(to right, rgba(212,175,55,0.1), transparent)",
              borderLeft: "2px solid #D4AF37",
              paddingLeft: "14px",
            } : {}}
          >
            {({ isActive }) => (
              <>
                <Icon className="w-[17px] h-[17px] flex-shrink-0"
                  weight={isActive ? "fill" : "regular"}
                  style={{ color: isActive ? "#D4AF37" : undefined }}
                />
                <span className="font-manrope">{t(labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-6 border-t border-white/[0.06]">
        <div className="flex items-center justify-between px-2 py-2.5 mb-2">
          <span className="text-xs text-zinc-500">{t("nav.light_mode")}</span>
          <ThemeToggle />
        </div>
        <div className="flex items-center gap-3 px-2 py-2 rounded-md">
          <Link to="/profile" className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-sm hover:opacity-80 transition-opacity flex-shrink-0">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </Link>
          <div className="flex-1 min-w-0">
            <Link to="/profile" className="text-sm font-semibold truncate block hover:text-yellow-400 transition-colors" data-testid="sidebar-user-name">{user?.name}</Link>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1.5">
              {user?.role === "admin"
                ? <span style={{ color: "#D4AF37" }}>Admin</span>
                : <>{user?.plan === "vip" && <Crown className="w-3 h-3 text-yellow-400" weight="fill" />}{user?.plan}</>
              }
            </div>
          </div>
        </div>
        <button
          data-testid="sidebar-logout"
          onClick={() => { logout(); nav("/login"); }}
          className="w-full mt-2 flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-red-400 hover:text-white hover:bg-red-500/15 border border-transparent hover:border-red-500/30"
        >
          <SignOut className="w-4 h-4 flex-shrink-0" weight="bold" />
          {t("nav.logout")}
        </button>
      </div>
    </aside>
  );
}
